import { useEffect, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

// «رندرِ واقعی» — قدمِ ۳: نمایشِ نتیجه‌ی سرور داخلِ همان صحنه.
//
// سرور (Blender) نورِ کامل (مستقیم + بازتابی) را در یک تصویر پخته است (lightmap.jpg) و برای هر سطح یک UVِ دوم
// (TEXCOORD_1 → در three: uv1) ساخته که می‌گوید هر نقطه کجای آن تصویر است. این‌جا هر سطحِ پخته‌شده یک
// MeshBasicMaterial می‌گیرد: رنگ/بافتِ خودش × نورِ پخته‌شده. چراغ‌های زنده‌ی صحنه روی این سطح‌ها اثری ندارند
// (نور همان است که سرور حساب کرده) و روی موبایل هم خیلی سبک است. شیشه و نوارهای LED (که پخته نشده‌اند و UVِ
// دوم ندارند) همان جنسِ خودشان را نگه می‌دارند.
//
// روشنایی: سرور نور را قبل از ذخیره در JPEG بر lightmapIntensity تقسیم کرده؛ three هم نورِ lightMap را بر π
// تقسیم می‌کند (مدلِ Lambert) — پس ضریب = lightmapIntensity × π. exposure برای تنظیمِ دستیِ کلی است.

function convertMaterial(mat, lightMap, intensity) {
  const basic = new THREE.MeshBasicMaterial({
    name: mat.name,
    color: mat.color ? mat.color.clone() : new THREE.Color(1, 1, 1),
    map: mat.map || null,
    lightMap,
    lightMapIntensity: intensity,
    side: mat.side,
    alphaTest: mat.alphaTest || 0,
  });
  basic.userData = { khNoRoomLight: true, khBaked: true }; // ضریبِ روشناییِ اتاق (roomLighting) رویش اعمال نشود
  return basic;
}

export function prepareBakedScene(root, lightMap, intensity) {
  const converted = new Map(); // هر جنس یک بار تبدیل شود
  const ceilings = [];
  // لنگه‌های در/پنجره در نسخه‌ی پخته دیده نمی‌شوند: جایشان لنگه‌های زنده‌ی ساختمان (که باز و بسته می‌شوند)
  // نشان داده می‌شود. در محاسبه‌ی نور همان حالتِ لحظه‌ی رندر حساب شده‌اند
  root.traverse((obj) => {
    if (obj.userData && obj.userData.khLeaf) obj.visible = false;
  });
  root.traverse((obj) => {
    if (obj.userData && obj.userData.khExportAlways) ceilings.push(obj);
    if (!obj.isMesh) return;
    obj.userData = { ...obj.userData, khNoExport: true };
    const mat = obj.material;
    if (!mat || Array.isArray(mat)) return;
    const baked = Boolean(obj.geometry?.attributes?.uv1) && !mat.transparent;
    if (!baked) {
      mat.userData = { ...mat.userData, khNoRoomLight: true };
      return;
    }
    if (!converted.has(mat)) converted.set(mat, convertMaterial(mat, lightMap, intensity));
    obj.material = converted.get(mat);
  });
  return { ceilings, materials: [...converted.values()] };
}

export function disposeTree(root) {
  root.traverse((obj) => {
    if (obj.geometry) obj.geometry.dispose();
    const mats = Array.isArray(obj.material) ? obj.material : obj.material ? [obj.material] : [];
    mats.forEach((m) => {
      Object.values(m).forEach((v) => v && v.isTexture && v.dispose());
      m.dispose();
    });
  });
}

// result: { resultUrl, lightmapUrl, meta }. onStatus('loading' | 'ready' | 'error', progress?)
// showCeiling: سقف فقط در اول‌شخص (در نمای پیش‌فرض جلوی دیدِ داخلِ خانه را می‌گیرد)
export function BakedModel({ result, showCeiling, exposure = 1, onStatus }) {
  const { invalidate } = useThree();
  const groupRef = useRef(null);
  const stateRef = useRef({ root: null, ceilings: [], materials: [], lightMap: null });
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;
  const showCeilingRef = useRef(showCeiling);
  showCeilingRef.current = showCeiling;
  const exposureRef = useRef(exposure);
  exposureRef.current = exposure;

  const baseIntensity = (Number(result?.meta?.lightmapIntensity) || 4) * Math.PI;

  useEffect(() => {
    if (!result?.resultUrl || !result?.lightmapUrl) return undefined;
    let cancelled = false;
    const report = (s, p) => onStatusRef.current && onStatusRef.current(s, p);
    report('loading', 0);

    const texPromise = new Promise((resolve, reject) => {
      new THREE.TextureLoader().load(result.lightmapUrl, resolve, undefined, reject);
    });
    const glbPromise = new Promise((resolve, reject) => {
      new GLTFLoader().load(
        result.resultUrl,
        resolve,
        (e) => {
          if (e.lengthComputable && e.total) report('loading', e.loaded / e.total);
        },
        reject
      );
    });

    Promise.all([glbPromise, texPromise])
      .then(([gltf, lightMap]) => {
        if (cancelled) {
          disposeTree(gltf.scene);
          lightMap.dispose();
          return;
        }
        lightMap.flipY = false; // مثلِ همه‌ی بافت‌های glTF
        lightMap.colorSpace = THREE.SRGBColorSpace;
        lightMap.channel = 1; // UVِ دوم (TEXCOORD_1)
        lightMap.generateMipmaps = true;
        lightMap.minFilter = THREE.LinearMipmapLinearFilter;
        lightMap.magFilter = THREE.LinearFilter;
        lightMap.needsUpdate = true;

        const root = gltf.scene;
        const { ceilings, materials } = prepareBakedScene(root, lightMap, baseIntensity * exposureRef.current);
        ceilings.forEach((c) => {
          c.visible = showCeilingRef.current;
        });
        stateRef.current = { root, ceilings, materials, lightMap };
        if (groupRef.current) groupRef.current.add(root);
        invalidate();
        report('ready', 1);
      })
      .catch(() => {
        if (!cancelled) report('error');
      });

    return () => {
      cancelled = true;
      const { root, lightMap } = stateRef.current;
      if (root) {
        if (root.parent) root.parent.remove(root);
        disposeTree(root);
      }
      if (lightMap) lightMap.dispose();
      stateRef.current = { root: null, ceilings: [], materials: [], lightMap: null };
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result?.resultUrl, result?.lightmapUrl]);

  useEffect(() => {
    stateRef.current.ceilings.forEach((c) => {
      c.visible = showCeiling;
    });
    invalidate();
  }, [showCeiling, invalidate]);

  useEffect(() => {
    stateRef.current.materials.forEach((m) => {
      m.lightMapIntensity = baseIntensity * exposure;
    });
    invalidate();
  }, [exposure, baseIntensity, invalidate]);

  return <group ref={groupRef} userData={{ khNoExport: true }} />;
}
